import { useQuery } from '@apollo/client';

import {
  PageFitnessSettingsQuery,
  UserFitnessConnectionStateQuery,
  UserFitnessConnectionStateQueryVariables,
} from '../../../types/__generated__';
import { useFullPageQuery, usePageOwner } from '../../hooks';
import { getMeasurementSystemFallback } from '../../utils/measurementSystem';
import { notNull } from '../../utils/typeGuards';
import PageFitnessSettings from './PageFitnessSettings.gql';
import UserFitnessConnectionState from './UserFitnessConnectionState.gql';

export type FitnessApplicationName = 'strava' | 'fitbit';

export function useFitnessConnectionSettings(
  application: FitnessApplicationName,
) {
  const {
    data: pageFitnessSettings,
    loading: pageFitnessSettingsLoading,
  } = usePageFitnessSettings(application);
  const {
    data: userFitnessConnectionState,
    loading: userFitnessConnectionStateLoading,
  } = useUserFitnessConnectionState();

  if (!pageFitnessSettings) {
    return {};
  }

  const viewer = userFitnessConnectionState?.viewer;
  const page = pageFitnessSettings.page;
  const settings = page?.application?.settings;
  const totals = page?.totals;

  const isConnected = Boolean(
    viewer?.thirdPartyApplications?.[application]?.isConnected,
  );
  const isPaused = Boolean(
    isConnected && settings?.subscribedActivities?.length === 0,
  );
  const hasSettings = Boolean(settings);

  return {
    status: {
      isLoading:
        pageFitnessSettingsLoading || userFitnessConnectionStateLoading,
      isConnected,
      isPaused,
      hasSettings,
    },
    settings: {
      showMap: settings?.showMap ?? false,
      measurementSystem:
        settings?.measurementSystem ?? getMeasurementSystemFallback(),
      subscribedActivities: (settings?.subscribedActivities ?? []).filter(
        notNull,
      ),
      showPhotos: settings?.showPhotos ?? false,
      showDuration: settings?.showDuration ?? false,
      showDistance: settings?.showDistance ?? false,
      showElevation: settings?.showElevation ?? false,
      showTotaliser: settings?.showTotaliser ?? false,
    },
    totals: {
      timeline: totals?.timeline ?? [],
      primaryMeasurementType:
        application === 'fitbit'
          ? 'steps'
          : totals?.primaryMeasurementType ?? 'distance',
    },
    displayOptions: totals?.displayOptions,
  };
}

function usePageFitnessSettings(application: FitnessApplicationName) {
  return useFullPageQuery<PageFitnessSettingsQuery>(PageFitnessSettings, {
    variables: {
      application,
    },
  });
}

function useUserFitnessConnectionState() {
  const { isPageOwner } = usePageOwner();

  return useQuery<
    UserFitnessConnectionStateQuery,
    UserFitnessConnectionStateQueryVariables
  >(UserFitnessConnectionState, {
    ssr: false,
    errorPolicy: 'all',
    skip: !isPageOwner,
    context: {
      credentials: 'include',
    },
  });
}
